import { computed } from 'vue';
import { useRemoteConfigStore } from '@/shared/firebase';
import { useAccountStore } from '@/stores/account';
import { STANDARD_CARD_COUNTRIES } from '../constants/smartCard.constant';
import { resolveCardAvailability } from '../utils/cardAvailability.util';
import type {
  CardAvailabilityConfig,
  UseCardAvailabilityReturn,
} from '../types/smartCard.type';

/**
 * Valida disponibilidad del dashboard de tarjetas según país del usuario.
 * - Países estándar (CL, CO): siempre habilitados, nunca offshore ni Onboarding Global.
 * - Resto de países: controlados por `feature_card_availability`, con la clave propia del
 *   país ganando sobre la clave `default` cuando ambas existen.
 * - `isGlobalOnboardingCountry` distingue el caso en que la habilitación vino de `default`
 *   (Onboarding Global) del caso en que el país tiene su propia clave explícita.
 */
export function useCardAvailability(): UseCardAvailabilityReturn {
  const accountStore = useAccountStore();
  const remoteConfigStore = useRemoteConfigStore();

  const countryCode = computed(() => accountStore.getCompanyCountry);

  const cardsEnabledConfig = remoteConfigStore.getRemoteConfigValue('feature_card_availability');

  const isStandardCountry = computed<boolean>(() => {
    const country = countryCode.value;

    if (!country) {
      return false;
    }

    return (STANDARD_CARD_COUNTRIES as readonly string[]).includes(country.toUpperCase());
  });

  const availabilityResolution = computed(() => {
    const country = countryCode.value;

    if (!country || isStandardCountry.value) {
      return null;
    }

    return resolveCardAvailability(
      country,
      cardsEnabledConfig.value as CardAvailabilityConfig | null | undefined
    );
  });

  const isCardAvailableForCountry = computed<boolean>(() => {
    if (isStandardCountry.value) {
      return true;
    }

    return availabilityResolution.value?.isEnabled ?? false;
  });

  const isOffshoreCountry = computed<boolean>(() => {
    if (isStandardCountry.value) {
      return false;
    }

    return availabilityResolution.value?.isEnabled ?? false;
  });

  const isGlobalOnboardingCountry = computed<boolean>(() => {
    const resolution = availabilityResolution.value;

    return Boolean(resolution?.isEnabled) && resolution?.source === 'default';
  });

  return {
    isCardAvailableForCountry,
    isOffshoreCountry,
    isGlobalOnboardingCountry,
  };
}
